import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import Navbar from "@/components/Navbar";
import { LOGO_DARK } from "@/assets";
import { Lock, Medal, ArrowRight } from "lucide-react";
import { usePageContent } from "@/lib/pageContent";

const LOGO = LOGO_DARK;
const EXAMPLE_SALE = 1425; // the reference sale used in every fee example on the mock

function money(cents) {
  const n = (cents || 0) / 100;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

function feeOnExample(pct) {
  return `$${((EXAMPLE_SALE * (pct || 0)) / 100).toFixed(2)}`;
}

/* One plan bullet. `off` = greyed (a thing this plan does NOT include). */
function Bullet({ off, children }) {
  return (
    <li className={`flex items-baseline gap-3 py-[7px] text-[16px] ${off ? "text-[#76797D]" : "text-[#DAD6CE]"}`}>
      <span className={off ? "text-[#4A4B4D]" : "text-[#57682A]"}>■</span>
      <span>{children}</span>
    </li>
  );
}

/* Deliberate bullet order (owner spec): greyed "no X" items come FIRST on Standard. */
function planBullets(t) {
  const fl = t.free_listings || 0;
  const bumps = t.free_priority_listings || 0;
  const savedUnlimited = t.saved_filters_mode === "unlimited";
  const savedCount = t.saved_search_limit || 0;
  const hasSaved = savedUnlimited || savedCount > 0;
  const rows = [];
  if (bumps === 0) rows.push(<Bullet key="nb" off>No listing bumps</Bullet>);
  if (!hasSaved) rows.push(<Bullet key="ns" off>No saved searches</Bullet>);
  rows.push(<Bullet key="fl"><b className="text-[#DAD6CE] font-semibold">{fl}</b> free listings per month</Bullet>);
  if (bumps > 0) rows.push(<Bullet key="bp"><b className="text-[#DAD6CE] font-semibold">{bumps}</b> listing bumps per month</Bullet>);
  if (savedUnlimited) rows.push(<Bullet key="sv"><b className="text-[#DAD6CE] font-semibold">Unlimited</b> saved searches with alerts</Bullet>);
  else if (savedCount > 0) rows.push(<Bullet key="sv"><b className="text-[#DAD6CE] font-semibold">{savedCount}</b> saved searches with alerts</Bullet>);
  rows.push(<Bullet key="vp">Verified buyer and seller profile</Bullet>);
  rows.push(<Bullet key="fh">Funds held until delivery, plus an inspection window</Bullet>);
  return rows;
}

const WHO = {
  standard: "For selling a few things a year.",
  elite: "For selling every month. Pays for itself in one sale.",
};

function PlanCard({ t, billing, standardPct, cta }) {
  const isPro = t.id === "elite";
  const monthly = t.monthly_price_cents;
  const annual = t.annual_price_cents;
  const bigCents = billing === "annual" ? annual : monthly;
  const period = billing === "annual" ? "/ yr" : "/ mo";
  const annLine = billing === "annual"
    ? <>or <b className="text-[#748A3D] font-semibold">{money(monthly)} a month</b></>
    : <>or <b className="text-[#748A3D] font-semibold">{money(annual)} a year</b> — two months free</>;
  const topColor = isPro ? "#748A3D" : "#8E9195";
  const rateColor = isPro ? "#748A3D" : "#8E9195";

  return (
    <div
      className="flex flex-col bg-[#111213] border border-[#2E2F31] p-7 pb-8"
      style={{ borderTop: `4px solid ${topColor}` }}
      data-testid={`tier-card-${t.id}`}
    >
      <span className={`self-start mb-3.5 font-mono font-bold text-[11px] tracking-[0.16em] uppercase px-2.5 py-[5px] ${isPro ? "bg-[#3A4A1E] text-[#CFE0A4]" : "bg-[#2A2B2D] text-[#76797D]"}`}>
        {isPro ? "Most popular" : "Start here"}
      </span>
      <div className="font-heading font-bold text-[34px] leading-none uppercase tracking-[0.02em] text-[#DAD6CE]">{t.label}</div>
      <div className="text-[15px] text-[#76797D] mt-2 min-h-[44px]">{WHO[t.id] || t.audience}</div>

      <div className="font-heading font-bold text-[70px] leading-[0.9] text-[#DAD6CE] mt-1.5" data-testid={`tier-price-${t.id}`}>
        {billing === "annual" && (
          <span className="align-middle mr-3 font-body font-normal text-[26px] text-[#6C6F73] line-through" data-testid={`tier-anchor-${t.id}`}>
            {money(monthly * 12)}
          </span>
        )}
        {money(bigCents)}<em className="not-italic font-body font-normal text-[19px] text-[#8E9195]"> {period}</em>
      </div>
      <div className="text-[15px] text-[#8E9195] mt-2.5">{annLine}</div>

      <div className="mt-6 border-t border-[#2E2F31] pt-5" data-testid={`tier-commission-${t.id}`}>
        <div className="font-heading font-bold text-[46px] leading-none" style={{ color: rateColor }}>{t.commission_pct}%</div>
        <div className="font-mono font-medium text-[11px] tracking-[0.16em] uppercase text-[#76797D] mt-1">Platform fee per sale</div>
        <div className="text-[14px] text-[#8E9195] mt-2">
          {feeOnExample(t.commission_pct)} on a ${EXAMPLE_SALE.toLocaleString()} sale.<br />
          {isPro && standardPct != null && <>Standard would be {feeOnExample(standardPct)}.<br /></>}
          Payment processing is separate.
        </div>
      </div>

      <ul className="mt-[22px] border-t border-[#2E2F31] pt-[18px] list-none">{planBullets(t)}</ul>

      <div className="mt-auto pt-6">{cta}</div>
    </div>
  );
}

// Buyer Pass — a PURCHASE-ONLY entry tier ($5, one-time, 7-day). Added leftmost; the existing
// seller-plan cards are untouched. Buyers only: full browsing + messaging + buying, no selling.
function BuyerPassCard({ user, pass, busy, onBuy }) {
  const active = !!pass?.active;
  const expDate = pass?.expires_at ? new Date(pass.expires_at).toLocaleDateString() : null;
  const cls = "block w-full text-center font-mono font-bold text-[13px] tracking-[0.16em] uppercase py-[15px] bg-[#CFE86B] border border-[#CFE86B] text-[#141A05] hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-default";
  return (
    <div className="flex flex-col bg-[#111213] border border-[#2E2F31] p-7 pb-8" style={{ borderTop: "4px solid #CFE86B" }} data-testid="tier-card-buyer-pass">
      <span className="self-start mb-3.5 font-mono font-bold text-[11px] tracking-[0.16em] uppercase px-2.5 py-[5px] bg-[#2A2B2D] text-[#CFE86B]">Just browsing</span>
      <div className="font-heading font-bold text-[34px] leading-none uppercase tracking-[0.02em] text-[#DAD6CE]">Buyer Pass</div>
      <div className="mt-2.5 flex items-start gap-2 border border-[#CFE86B]/40 bg-[#CFE86B]/[0.07] px-3 py-2" data-testid="buyer-pass-buyonly-tag">
        <Lock className="w-3.5 h-3.5 text-[#CFE86B] mt-[2px] shrink-0" />
        <span className="font-mono font-bold text-[11px] tracking-[0.1em] uppercase text-[#CFE86B] leading-snug">Browse &amp; buy only — not for selling</span>
      </div>
      <div className="text-[15px] text-[#76797D] mt-2.5">Here to buy, not sell? Full access for a week.</div>

      <div className="font-heading font-bold text-[70px] leading-[0.9] text-[#DAD6CE] mt-1.5" data-testid="tier-price-buyer-pass">
        $5<em className="not-italic font-body font-normal text-[19px] text-[#8E9195]"> one-time</em>
      </div>
      <div className="text-[15px] text-[#8E9195] mt-2.5">7 days of full access — no auto-renew.</div>

      <div className="mt-6 border-t border-[#2E2F31] pt-5">
        <div className="font-heading font-bold text-[46px] leading-none text-[#8E9195]">$0</div>
        <div className="font-mono font-medium text-[11px] tracking-[0.16em] uppercase text-[#76797D] mt-1">You're not selling</div>
        <div className="text-[14px] text-[#8E9195] mt-2">A pass is for buyers. To sell gear, pick a seller plan instead.</div>
      </div>

      <ul className="mt-[22px] border-t border-[#2E2F31] pt-[18px] list-none">
        <Bullet>See every listing — full price and seller</Bullet>
        <Bullet>Message any seller and make offers</Bullet>
        <Bullet>Buy with buyer protection + delivery protection</Bullet>
        <Bullet off>No selling or listing gear</Bullet>
      </ul>

      <div className="mt-auto pt-6">
        {active ? (
          <button disabled data-testid="cta-buyer-pass" className={cls}>{expDate ? `Active — until ${expDate}` : "Active"}</button>
        ) : !user ? (
          <button onClick={onBuy} data-testid="cta-buyer-pass" className={cls}>
            <Lock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Get Buyer Pass
          </button>
        ) : (
          <button onClick={onBuy} disabled={busy} data-testid="cta-buyer-pass" className={cls}>
            <Lock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Get Buyer Pass
          </button>
        )}
      </div>
    </div>
  );
}

function NeedMoreCard() {
  return (
    <div className="flex flex-col bg-[#0D0E0F] border border-[#2E2F31] p-7 pb-8" style={{ borderTop: "4px solid #57682A" }} data-testid="tier-card-need-more">
      <span className="self-start mb-3.5 font-mono font-bold text-[11px] tracking-[0.16em] uppercase px-2.5 py-[5px] bg-[#2A2B2D] text-[#849064]">Coming soon</span>
      <div className="font-heading font-bold text-[34px] leading-none uppercase tracking-[0.02em] text-[#DAD6CE]">Business &amp; Dealer</div>
      <div className="text-[15px] text-[#8E9195] mt-3 leading-relaxed flex-1">
        For shops, dealers and FFL/SOTs. OpticSwap for business accounts is on the way — join the waitlist and we'll reach out as accounts open up.
      </div>
      <div className="pt-2">
        <Link
          to="/business?from=pricing"
          data-testid="tier-need-more-contact"
          className="flex items-center justify-center gap-2 w-full text-center font-mono font-bold text-[13px] tracking-[0.16em] uppercase py-[15px] border border-[#3E3F42] text-[#DAD6CE] hover:border-[#748A3D] transition-colors"
        >
          Join the Waitlist <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Toggle({ billing, setBilling }) {
  const seg = (v, label) => (
    <button
      key={v}
      onClick={() => setBilling(v)}
      data-testid={`billing-${v}`}
      className={`font-mono font-medium text-[12px] tracking-[0.14em] uppercase px-5 py-[11px] transition-colors ${billing === v ? "bg-[#3E3F42] text-[#DAD6CE]" : "text-[#76797D] hover:text-[#DAD6CE]"}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center justify-center mt-8" style={{ gap: "14px" }}>
      <div className="inline-flex border border-[#2E2F31] bg-[#0D0E0F]" data-testid="pricing-billing-toggle">
        {seg("monthly", "Monthly")}
        {seg("annual", "Annual")}
      </div>
      {billing === "annual" && (
        <div data-testid="pricing-annual-bonus" aria-hidden="true"
          className="inline-flex items-center select-none pointer-events-none"
          style={{ border: "1px solid #7A6428", background: "linear-gradient(180deg, #2A2213, #1A150B)", padding: "6px 11px", gap: "8px" }}>
          <span className="font-heading" style={{ fontWeight: 800, fontSize: "17px", color: "#E8C46A", lineHeight: 1 }}>2</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9.5px", letterSpacing: ".11em", textTransform: "uppercase", color: "#C7A75A" }}>months free</span>
        </div>
      )}
    </div>
  );
}

export default function Pricing() {
  const { user, buyerPassEnabled } = useAuth();
  const { get } = usePageContent("pricing");
  const navigate = useNavigate();
  const [billing, setBilling] = useState("annual");
  const [tiers, setTiers] = useState([]);
  const [mem, setMem] = useState(null);
  const [pass, setPass] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmPass, setConfirmPass] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/config/tiers").then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (user) {
      api.get("/membership/me").then(({ data }) => setMem(data)).catch(() => {});
      api.get("/buyer-pass/me").then(({ data }) => setPass(data)).catch(() => {});
    } else { setMem(null); setPass(null); }
  }, [user]);

  const doBuyerPassCheckout = async (confirm = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/buyer-pass/checkout", { origin_url: window.location.origin, confirm });
      const url = data.checkout_url || data.url;
      if (url) { window.location.href = url; return; }
      toast.error("Could not start checkout.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Server-side guard: the account already has full access. Show the warning dialog.
      if (e.response?.status === 409 && detail?.code === "already_has_access") {
        setConfirmPass(true);
        return;
      }
      toast.error(formatError(detail) || "Payments are temporarily unavailable.");
    } finally { setBusy(false); }
  };

  const onBuyerPass = async () => {
    if (!user) {
      // A pass must attach to an account. Remember the intent, send them to sign up / sign in,
      // and resume straight into Stripe Checkout once they're authenticated (see the effect below).
      try { localStorage.setItem("os_after_login", "/pricing?buy=buyer_pass"); } catch {}
      navigate("/join");
      return;
    }
    // Pre-checkout guard: an account that already has full marketplace access doesn't need a pass —
    // warn first and let them cancel, or explicitly confirm (gift / extend).
    if (pass?.has_access) { setConfirmPass(true); return; }
    doBuyerPassCheckout(false);
  };

  // Resume a guest's Buyer-Pass intent after they authenticate: consumeAfterLogin() lands them on
  // /pricing?buy=buyer_pass, and this kicks off the checkout once the session + pass state are known.
  const bpAutoRef = useRef(false);
  useEffect(() => {
    if (params.get("buy") !== "buyer_pass") return;
    if (!buyerPassEnabled || !user || pass == null) return; // wait for session + pass state
    if (bpAutoRef.current) return;
    bpAutoRef.current = true;
    const next = new URLSearchParams(params);
    next.delete("buy");
    setParams(next, { replace: true });
    if (pass.active) { toast.info("Your Buyer Pass is already active."); return; }
    onBuyerPass();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user, pass, buyerPassEnabled]);

  const onSubscribe = async (tier, interval) => {
    setBusy(true);
    try {
      const { data } = await api.post("/membership/checkout", { tier, interval, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not start checkout.");
    } finally { setBusy(false); }
  };
  const onSwitch = async (tier, interval) => {
    setBusy(true);
    try {
      const { data } = await api.post("/membership/change", { tier, interval });
      toast.success(data.message || "Plan updated.");
      const r = await api.get("/membership/me");
      setMem(r.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not change plan.");
    } finally { setBusy(false); }
  };

  const standard = tiers.find((t) => t.id === "standard");
  const standardPct = standard?.commission_pct;
  const order = ["standard", "elite"];
  const shown = order.map((id) => tiers.find((t) => t.id === id)).filter(Boolean);

  const interval = billing === "annual" ? "year" : "month";
  const intervalQ = billing === "annual" ? "annual" : "monthly";

  const ctaFor = (t) => {
    const label = `Choose ${t.label}`;
    const gold = "block w-full text-center font-mono font-bold text-[13px] tracking-[0.16em] uppercase py-[15px] bg-[#57682A] border border-[#57682A] text-[#EDF2DE] hover:bg-[#748A3D] transition-colors disabled:opacity-60 disabled:cursor-default";
    const outline = "block w-full text-center font-mono font-bold text-[13px] tracking-[0.16em] uppercase py-[15px] border border-[#2E2F31] text-[#DAD6CE] hover:border-[#748A3D] transition-colors disabled:opacity-60 disabled:cursor-default";
    const cls = t.id === "elite" ? gold : outline;
    const testId = `cta-tier-${t.id}`;

    // Buttons LIVE (2026-06): checkout builds the amount INLINE from the Tier Permissions panel
    // (no stored Price IDs), proven in test mode at $10/$100 and $25/$250. Beta testers are comped
    // 90-day Pro, so a real charge only happens by deliberate choice.
    if (!user) {
      return <Link to={`/register?plan=${t.id}&interval=${intervalQ}`} data-testid={testId} className={cls}>{label}</Link>;
    }
    if (mem?.comped) {
      return <button disabled data-testid={testId} className={cls}>{mem.tier === t.id ? "Included" : "Comp on file"}</button>;
    }
    const paying = mem && ["active", "trialing", "past_due", "canceling"].includes(mem.status);
    if (paying) {
      if (mem.tier === t.id) return <button disabled data-testid={testId} className={cls}>Current Plan</button>;
      return <button onClick={() => onSwitch(t.id, interval)} disabled={busy} data-testid={testId} className={cls}>Switch to {t.label}</button>;
    }
    return <button onClick={() => onSubscribe(t.id, interval)} disabled={busy} data-testid={testId} className={cls}>{label}</button>;
  };

  return (
    <div className="min-h-screen bg-[#050506] text-[#DAD6CE]">
      <Navbar />
      <div className="max-w-[1180px] mx-auto px-6 sm:px-11 pt-14 pb-24" data-testid="pricing-page">
        {/* HEADER — title left, dark logo top-right */}
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="font-heading font-bold uppercase text-5xl sm:text-6xl leading-none tracking-[0.02em] text-[#DAD6CE]">{get("heading", "Plans")}</h1>
            <p className="mt-3.5 text-[18px] text-[#8E9195] max-w-[640px]">
              {get("subhead", "Every plan lists, buys and sells. The plan sets your platform fee and how much you get for free each month. Payment processing is billed separately, at cost.")}
            </p>
          </div>
          <img src={LOGO} alt="OpticSwap" className="w-[156px] h-auto shrink-0 hidden sm:block" data-testid="pricing-header-logo" />
        </div>

        <Toggle billing={billing} setBilling={setBilling} />

        {mem?.comp_grant_type === "founding_6mo" && mem?.comped && mem?.comped_expires_at && (
          <div data-testid="founding-comp-banner"
            className="mt-8 flex items-center gap-3 border px-5 py-4"
            style={{ borderColor: "#55451C", background: "#14110A" }}>
            <Medal className="w-5 h-5 shrink-0" style={{ color: "#C8A64E" }} />
            <div>
              <div className="font-mono uppercase tracking-[0.12em] text-[13px]" style={{ color: "#C8A64E" }}>
                Founding Pro — comped until {new Date(mem.comped_expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
              </div>
              <p className="text-[13px] text-[#9A907A] mt-0.5">
                A founding-member benefit — full Pro access at no charge during your 6-month window. You still pay the Pro 3.5% platform fee, same as any Pro member.
              </p>
            </div>
          </div>
        )}

        {/* CARDS — Buyer Pass (leftmost) is added only when enabled; existing seller cards unchanged. */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${buyerPassEnabled ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5 mt-9 items-stretch`} data-testid="tier-grid">
          {buyerPassEnabled && <BuyerPassCard user={user} pass={pass} busy={busy} onBuy={onBuyerPass} />}
          {shown.map((t) => (
            <PlanCard key={t.id} t={t} billing={billing} standardPct={standardPct} cta={ctaFor(t)} />
          ))}
          <NeedMoreCard />
        </div>

        {/* PAYMENT PROCESSING */}
        <div className="mt-8 bg-[#0D0E0F] border border-[#2E2F31] p-7" data-testid="pricing-processing">
          <div className="font-mono font-medium text-[11px] tracking-[0.16em] uppercase text-[#8E9195]">Payment processing, billed separately at cost</div>
          <div className="grid sm:grid-cols-2 gap-6 mt-5">
            {[
              { k: "Bank transfer", v: "0.8%", d: `$5.00 on a $${EXAMPLE_SALE.toLocaleString()} sale (capped at $5)` },
              { k: "Card", v: "2.9% + 30¢", d: `$41.63 on a $${EXAMPLE_SALE.toLocaleString()} sale` },
            ].map((p) => (
              <div key={p.k} className="border-l-2 border-[#57682A] pl-4">
                <div className="font-heading font-bold text-[22px] uppercase tracking-[0.02em] text-[#DAD6CE]">{p.k}</div>
                <div className="font-heading font-bold text-[30px] leading-none text-[#748A3D] mt-1">{p.v}</div>
                <div className="text-[14px] text-[#8E9195] mt-1">{p.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-[#2E2F31] text-[14px] text-[#8E9195]">
            Not a markup. This is what the card networks and the bank charge to move the money and pay the seller out, passed through at cost.
          </div>
        </div>

        {/* FOOTER STRIP */}
        <div className="mt-6 bg-[#0D0E0F] border border-[#2E2F31] grid grid-cols-1 sm:grid-cols-3" data-testid="pricing-footer-strip">
          {[
            { t: "Extra listings", d: "$5 each, any plan" },
            { t: "Extra bumps", d: "$2 each, any plan" },
            { t: "Switch anytime", d: "Upgrades take effect on the next sale" },
          ].map((f, i) => (
            <div key={f.t} className={`p-6 border-l-2 border-[#57682A] ${i > 0 ? "sm:border-t-0 border-t border-t-[#2E2F31]" : ""}`}>
              <div className="font-heading font-bold text-[20px] uppercase tracking-[0.02em] text-[#DAD6CE]">{f.t}</div>
              <div className="text-[14px] text-[#8E9195] mt-1.5">{f.d}</div>
            </div>
          ))}
        </div>

        {/* WHY THE FEE EXISTS */}
        <div className="mt-6 bg-[#0D0E0F] border border-[#2E2F31] p-7" data-testid="pricing-why-fee">
          <div className="font-mono font-medium text-[11px] tracking-[0.16em] uppercase text-[#8E9195]">Why the fee exists</div>
          <h3 className="mt-3 font-heading font-bold text-[24px] sm:text-[28px] uppercase tracking-[0.02em] leading-tight text-[#DAD6CE]">Where does your membership fee go?</h3>
          <p className="mt-4 max-w-[760px] text-[14px] leading-relaxed text-[#8E9195]">
            Running the platform costs money whether anyone's actively buying or selling — ID verification, secure payment processing, keeping funds held safely until delivery confirms, hosting, all of it. The membership fee covers that baseline so your account stays verified and protected even in a slow month. Buy or sell a couple times and it pays for itself many times over in what you're actually protected against.
          </p>
        </div>

        {/* ALREADY-HAS-ACCESS WARNING — pre-checkout guard for the Buyer Pass */}
        {confirmPass && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="buyer-pass-confirm">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setConfirmPass(false)} />
            <div className="relative w-full max-w-md bg-[#111213] border border-[#2E2F31] p-6">
              <div className="flex items-center gap-2.5 mb-3">
                <Lock className="w-5 h-5 text-[#CFE86B]" />
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[#E8E8EA]">You already have access</h3>
              </div>
              <p className="text-[14px] text-[#C6C9CD] leading-relaxed" data-testid="buyer-pass-confirm-msg">
                You already have full marketplace access via {pass?.access_via || "your account"}. A Buyer Pass isn't needed.
              </p>
              <p className="text-[13px] text-[#8B8D90] mt-3 leading-relaxed">
                You can still buy one — for example to gift it or extend access later — but it won't add anything you don't already have.
              </p>
              <div className="mt-6 flex gap-3">
                <button onClick={() => setConfirmPass(false)} data-testid="buyer-pass-confirm-cancel"
                  className="flex-1 font-mono font-bold text-[12px] tracking-[0.16em] uppercase py-3 border border-[#3E3F42] text-[#DAD6CE] hover:border-[#748A3D] transition-colors">
                  Cancel
                </button>
                <button onClick={() => { setConfirmPass(false); doBuyerPassCheckout(true); }} disabled={busy} data-testid="buyer-pass-confirm-buy"
                  className="flex-1 font-mono font-bold text-[12px] tracking-[0.16em] uppercase py-3 bg-[#CFE86B] text-[#141A05] hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
                  Buy anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
